import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gsap } from "gsap";
import { cursosAPI, progressoAPI, modulosAPI } from '../services/api';
import VideoPlayer from '../components/VideoPlayer';
import ModalMatricula from '../components/ModalMatricula';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Play, 
  Clock, 
  ArrowLeft, 
  BookOpen, 
  CheckCircle, 
  PlayCircle,
  ChevronDown,
  ChevronUp,
  Layers
} from 'lucide-react';

const API_BASE_URL = 'http://localhost:5000';

function AssistirCurso() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { usuario, loading: authLoading } = useAuth();
  const containerRef = useRef(null);
  const [curso, setCurso] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progressoCurso, setProgressoCurso] = useState(null);
  const [videoSelecionado, setVideoSelecionado] = useState(null);
  const [modulosAbertos, setModulosAbertos] = useState({});
  const [matriculado, setMatriculado] = useState(null);
  const [showModalMatricula, setShowModalMatricula] = useState(false);
  const [carregandoMatricula, setCarregandoMatricula] = useState(false);
  const [videoKey, setVideoKey] = useState(0); // Para forçar re-render do VideoPlayer

  useEffect(() => {
    if (!authLoading && usuario) {
      carregarDados();
    }
  }, [id, authLoading, usuario]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Primeiro verificar se está matriculado
      const matriculaResponse = await cursosAPI.verificarMatricula(id);
      const isMatriculado = matriculaResponse.data.matriculado;
      setMatriculado(isMatriculado);
      
      // Se não estiver matriculado, mostrar modal e não carregar o resto
      if (!isMatriculado) {
        const cursoResponse = await cursosAPI.obter(id);
        setCurso(cursoResponse.data);
        setShowModalMatricula(true);
        setLoading(false);
        return;
      }
      
      // Se estiver matriculado, carregar tudo normalmente
      const [cursoResponse, modulosResponse] = await Promise.all([
        cursosAPI.obter(id),
        modulosAPI.listarPorCurso(id)
      ]);
      
      setCurso(cursoResponse.data);
      const modulosData = modulosResponse.data.modulos || [];
      setModulos(modulosData);
      
      // Abrir primeiro módulo por padrão
      if (modulosData.length > 0) {
        setModulosAbertos({ [modulosData[0].id]: true });
        
        // Selecionar primeiro vídeo do primeiro módulo
        if (modulosData[0].videos && modulosData[0].videos.length > 0) {
          setVideoSelecionado(modulosData[0].videos[0]);
        }
      }
      
      await carregarProgressoCurso();
      
    } catch (err) {
      setError('Erro ao carregar dados do curso');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const carregarProgressoCurso = async () => {
    try {
      if (usuario?.id) {
        const response = await progressoAPI.obterProgressoCurso(id, usuario.id);
        setProgressoCurso(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar progresso do curso:', err);
    }
  };

  const selecionarVideo = (video) => {
    setVideoSelecionado(video);
    setVideoKey(prev => prev + 1); // Força re-render do VideoPlayer
  };

  const toggleModulo = (moduloId) => {
    setModulosAbertos(prev => ({
      ...prev,
      [moduloId]: !prev[moduloId]
    }));
  };

  const handleMatricular = async () => {
    try {
      setCarregandoMatricula(true);
      await cursosAPI.matricular(id);
      
      // Fechar modal e recarregar dados
      setShowModalMatricula(false);
      setMatriculado(true);
      
      // Recarregar dados do curso com conteúdo completo
      await carregarDados();
      
    } catch (err) {
      console.error('Erro ao matricular:', err);
      alert('Erro ao realizar matrícula. Tente novamente.');
    } finally {
      setCarregandoMatricula(false);
    }
  };

  const handleFecharModal = () => {
    setShowModalMatricula(false);
    navigate(`/curso/${id}`); // Redirecionar para página de detalhes do curso
  };

  // Calcular totais de vídeos
  const getTotalVideos = () => {
    return modulos.reduce((total, modulo) => total + (modulo.videos?.length || 0), 0);
  };

  // Verificar se usuário está autenticado
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
          <p className="text-white text-lg">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!usuario) {
    navigate('/auth');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
          <p className="text-white text-lg">Carregando curso...</p>
        </div>
      </div>
    );
  }

  if (error || (!curso && !loading)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mb-4">
            <BookOpen className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">Curso não encontrado</h2>
          <p className="text-slate-400">Não foi possível carregar o curso.</p>
          <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  // Se não estiver matriculado, mostrar apenas o modal
  if (matriculado === false && curso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <ModalMatricula
          isOpen={showModalMatricula}
          onClose={handleFecharModal}
          curso={curso}
          onMatricular={handleMatricular}
          carregandoMatricula={carregandoMatricula}
        />
      </div>
    );
  }

  const progressoGeral = progressoCurso?.progresso_geral || { porcentagem: 0, videos_concluidos: 0, total_videos: 0 };
  const totalVideos = getTotalVideos();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div ref={containerRef}>
        {/* Header fixo */}
        <div className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/curso/${id}`)}
                  className="text-slate-400 hover:text-white hover:bg-slate-800/50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <div className="text-slate-400">/</div>
                <div className="flex items-center space-x-2">
                  <PlayCircle className="w-4 h-4 text-red-400" />
                  <span className="text-white font-medium">{curso.titulo}</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {videoSelecionado && (
                  <div className="text-sm text-slate-300">
                    {videoSelecionado.titulo}
                  </div>
                )}
                <div className="text-sm text-slate-400">
                  {progressoGeral.videos_concluidos}/{totalVideos} concluídos
                </div>
              </div>
            </div>
            
            <div className="mt-3">
              <Progress 
                value={progressoGeral.porcentagem} 
                className="h-2" 
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-0 min-h-screen">
          {/* Player de Vídeo */}
          <div className="xl:col-span-3">
            {videoSelecionado ? (
              <div className="video-container p-6">
                <VideoPlayer 
                  key={videoKey} // Força re-render quando muda o vídeo
                  video={videoSelecionado} 
                  cursoId={id} 
                  usuarioId={usuario?.id}
                  onProgressUpdate={carregarProgressoCurso}
                />
                
                <div className="mt-6 bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-6">
                  <h2 className="text-2xl font-bold text-white mb-2">{videoSelecionado.titulo}</h2>
                  {videoSelecionado.descricao && (
                    <p className="text-slate-300 leading-relaxed">{videoSelecionado.descricao}</p>
                  )}
                  
                  <div className="flex items-center gap-4 mt-4 text-sm text-slate-400">
                    {videoSelecionado.duracao && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>
                          {Math.floor(videoSelecionado.duracao / 60)}:{(videoSelecionado.duracao % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PlayCircle className="w-10 h-10 text-slate-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Selecione um vídeo</h3>
                  <p className="text-slate-400">Escolha um vídeo na navegação ao lado para começar</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar de Navegação - CORRIGIDA PARA MÓDULOS */}
          <div className="xl:col-span-1 border-l border-slate-800/50 bg-slate-900/30 backdrop-blur-xl">
            <div className="p-4 border-b border-slate-800/50">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-2">
                <Layers className="w-5 h-5 text-red-400" />
                Conteúdo do Curso
              </h3>
              <p className="text-sm text-slate-400">
                {modulos.length} módulos • {totalVideos} vídeos
              </p>
            </div>

            <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
              {modulos.length > 0 ? (
                modulos.map((modulo) => (
                  <div key={modulo.id} className="border-b border-slate-800/30">
                    {/* Cabeçalho do Módulo */}
                    <div
                      className="p-4 cursor-pointer hover:bg-slate-800/30 transition-colors"
                      onClick={() => toggleModulo(modulo.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-white text-sm mb-1">
                            {modulo.nome || modulo.titulo || `Módulo ${modulo.id}`}
                          </h4>
                          <p className="text-xs text-slate-400">
                            {modulo.videos?.length || 0} vídeos
                          </p>
                        </div>
                        <div className="text-slate-400">
                          {modulosAbertos[modulo.id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Vídeos do Módulo */}
                    {modulosAbertos[modulo.id] && modulo.videos && (
                      <div className="bg-slate-950/30">
                        {modulo.videos.map((video, index) => {
                          const progressoVideo = progressoCurso?.progresso_videos?.find(p => p.video_id === video.id);
                          const isAtivo = videoSelecionado?.id === video.id;
                          const porcentagemVideo = progressoVideo && video.duracao 
                            ? Math.round((progressoVideo.tempo_assistido / video.duracao) * 100) 
                            : 0;
                          
                          return (
                            <div 
                              key={video.id} 
                              className={`p-3 mx-2 my-1 rounded-lg cursor-pointer transition-all duration-200 ${
                                isAtivo 
                                  ? 'bg-red-600/20 border border-red-600/30' 
                                  : 'hover:bg-slate-800/50'
                              }`}
                              onClick={() => selecionarVideo(video)}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-1">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                                    isAtivo 
                                      ? 'bg-red-600 text-white' 
                                      : progressoVideo?.concluido 
                                        ? 'bg-green-600 text-white' 
                                        : 'bg-slate-700 text-slate-300'
                                  }`}>
                                    {progressoVideo?.concluido ? (
                                      <CheckCircle className="w-3 h-3" />
                                    ) : isAtivo ? (
                                      <Play className="w-2 h-2" />
                                    ) : (
                                      index + 1
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <h5 className={`font-medium text-xs mb-1 truncate ${
                                    isAtivo ? 'text-white' : 'text-slate-200'
                                  }`}>
                                    {video.titulo}
                                  </h5>
                                  
                                  <div className="flex items-center justify-between text-xs text-slate-400">
                                    {video.duracao && (
                                      <div className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        <span>
                                          {Math.floor(video.duracao / 60)}:{(video.duracao % 60).toString().padStart(2, '0')}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {progressoVideo && porcentagemVideo > 0 && (
                                      <span className="text-green-400">{porcentagemVideo}%</span>
                                    )}
                                  </div>
                                  
                                  {progressoVideo && video.duracao && porcentagemVideo > 0 && (
                                    <div className="mt-2">
                                      <div className="w-full bg-slate-700 rounded-full h-1">
                                        <div
                                          className="bg-green-500 h-1 rounded-full transition-all duration-300"
                                          style={{ width: `${porcentagemVideo}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-6 text-center">
                  <p className="text-slate-400">Nenhum módulo disponível</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de matrícula */}
      <ModalMatricula
        isOpen={showModalMatricula}
        onClose={handleFecharModal}
        curso={curso}
        onMatricular={handleMatricular}
        carregandoMatricula={carregandoMatricula}
      />
    </div>
  );
}

export default AssistirCurso; 